import { cn } from '@/shared/lib'

// Полная палитра токенов — литералы (не `dept-${i}`), иначе Tailwind-сканер их не
// увидит. Индекс выбирается детерминированным hash'ом seed'а.
const DOT = [
  'bg-dept-1',
  'bg-dept-2',
  'bg-dept-3',
  'bg-dept-4',
  'bg-dept-5',
  'bg-dept-6',
  'bg-dept-7',
  'bg-dept-8',
] as const

const TEXT = [
  'text-dept-1',
  'text-dept-2',
  'text-dept-3',
  'text-dept-4',
  'text-dept-5',
  'text-dept-6',
  'text-dept-7',
  'text-dept-8',
] as const

// Стабильный цвет отдела: hash по seed (id предпочтителен — переживает переименование;
// иначе имя) → индекс палитры.
function paletteIndex(seed: string): number {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h) % DOT.length
}

interface DepartmentBadgeProps {
  // Подпись отдела (имя из справочника). null/undefined → бейдж не рендерится.
  name: string | null | undefined
  // Источник цвета (id отдела, если есть — стабильнее при переименовании). По
  // умолчанию — имя.
  seed?: string | undefined
  variant?: 'dot' | 'chip'
  className?: string
}

// Тихий классификатор отдела (docs/design/README.md §4): приглушённый dept-токен,
// не accent. dot — точка у имени/доски; chip — soft-метка. Агностичен к справочнику:
// принимает готовые имя+seed, резолв id→имя — на уровне страницы/виджета.
export function DepartmentBadge({ name, seed, variant = 'chip', className }: DepartmentBadgeProps) {
  if (!name) return null
  const i = paletteIndex(seed ?? name)
  if (variant === 'dot') {
    return (
      <span
        role="img"
        aria-label={name}
        title={name}
        className={cn('inline-block size-2 shrink-0 rounded-pill', DOT[i], className)}
      />
    )
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-control bg-bg-raised px-2 py-0.5 font-mono text-xs',
        TEXT[i],
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-pill', DOT[i])} aria-hidden />
      {name}
    </span>
  )
}

interface DepartmentBadgesProps {
  // Набор имён отделов доски (departments[]). Пустой массив → ничего не рендерим.
  names: string[] | null | undefined
  variant?: 'dot' | 'chip'
  className?: string
}

// Ряд бейджей отделов доски: у доски несколько отделов (free-form). Каждый бейдж
// красится своим стабильным цветом (seed = имя). dot — компактный ряд точек в
// карточке/списке; chip — soft-метки в шапке.
export function DepartmentBadges({ names, variant, className }: DepartmentBadgesProps) {
  if (!names || names.length === 0) return null
  return (
    <>
      {names.map((name) => (
        <DepartmentBadge
          key={name}
          name={name}
          {...(variant ? { variant } : {})}
          {...(className ? { className } : {})}
        />
      ))}
    </>
  )
}
